// ─── Garden Adventure content pack (Adventure 4, 2026-08-18) ───
// Source of truth: "Garden Adventure — Speaking Tutor Q&A" Google Doc
// (approved by Natalya per Polina 2026-08-18 — the audio freeze point;
// rainbow questions KEPT, slots 7–8).
//
// Session shape (FIXED slots, 10 questions — script's Question flow):
//   1. name a fruit      2. name a vegetable          (random, bank draw)
//   3-5. three color questions on NEW items           (no repeats, ★ favored)
//   6. taste — peach (sweet) or lemon (sour)          (random)
//   7. "How many colors are in the rainbow?"          (fixed — count tier)
//   8. "Can you name the colors in the rainbow?"      (fixed — list tier, NEW)
//   9. "Do you like fruits/vegetables?"               (random — yesno tier)
//   10. "What is your favorite fruit/vegetable?"      (OPPOSITE of 9 — the
//       favorite finale, which is DYNAMIC here for the first time and earns
//       a star, so the star row still totals 10)
//
// Engine pieces added for this pack (all gated — Farm/Playground/School
// byte-for-byte behavior unchanged):
//   • 'list' tier — name ≥listMin of card.listWords (rainbow colors), local
//   • ADVENTURE.yesNoVerb — the yes/no ladder sentence verb ('like' here;
//     School keeps the 'have' default)
//   • ADVENTURE.favorite may be a FUNCTION → resolved per session (fruit vs
//     vegetable, opposite of slot 9) + favorite.earnsStar
//
// WORDING vs THE DOC (flagged to Natalya): the bank writes singular answers
// ("This is a banana.") but her card images show MULTIPLES for everything
// except lemon, peach and corn — so, exactly like Farm's multi cards, those
// items teach the plural ("These are bananas."), and the color questions use
// "are these" forms. Peppers.png shows red AND yellow peppers → its color
// answer is "red and yellow" (either word counts).
//
// MISSING IMAGES (not in the Drive folder): strawberry, pear, pumpkin.
// Their bank entries are commented out below — uncomment + drop the image in
// public/images/garden/ + rerun tts/gen-garden-audio.mjs when they arrive.
// All three still count as favorite-question answers (pre-baked audio).

// ── The fruits & vegetables bank ──
// count>1 → "What are these? / These are X." (matches the picture).
// article: '' → no article ("This is corn."). star → multi-color item the
// script says to favor for the color slots.
const bank = [
  // fruits
  { id: 'gar-apples', word: 'apple', plural: 'apples', count: 3, cat: 'fruit', color: 'red', star: true },
  { id: 'gar-bananas', word: 'banana', plural: 'bananas', count: 3, cat: 'fruit', color: 'yellow' },
  // Grapes: photo reads clearly blue → teach BLUE, still accept purple
  // (Natalya 2026-08-18; taught word flipped to blue 2026-10-20, tracker #5).
  { id: 'gar-grapes', word: 'grape', plural: 'grapes', count: 2, cat: 'fruit', color: 'blue and purple', colorWord: 'blue', star: true },
  { id: 'gar-lemon', word: 'lemon', plural: 'lemons', count: 1, cat: 'fruit', color: 'yellow' },
  { id: 'gar-oranges', word: 'orange', plural: 'oranges', count: 2, cat: 'fruit', color: 'orange' },
  { id: 'gar-strawberries', word: 'strawberry', plural: 'strawberries', count: 3, cat: 'fruit', color: 'red' },
  { id: 'gar-pears', word: 'pear', plural: 'pears', count: 3, cat: 'fruit', color: 'green' },
  // Juices (Natalya 2026-08-18: "let's do orange juice or apple juice" for a
  // What-is-this question). Naming only — no color question on a drink.
  { id: 'gar-orange-juice', word: 'orange juice', plural: 'orange juice', count: 1, cat: 'fruit', article: '', extraWords: ['juice'] },
  { id: 'gar-apple-juice', word: 'apple juice', plural: 'apple juice', count: 1, cat: 'fruit', article: '', extraWords: ['juice'] },
  // vegetables
  { id: 'gar-carrots', word: 'carrot', plural: 'carrots', count: 3, cat: 'veg', color: 'orange' },
  { id: 'gar-cucumbers', word: 'cucumber', plural: 'cucumbers', count: 3, cat: 'veg', color: 'green' },
  { id: 'gar-potatoes', word: 'potato', plural: 'potatoes', count: 3, cat: 'veg', color: 'brown' },
  { id: 'gar-corn', word: 'corn', plural: 'corn', count: 1, cat: 'veg', color: 'yellow', article: '' },
  { id: 'gar-tomatoes', word: 'tomato', plural: 'tomatoes', count: 3, cat: 'veg', color: 'red', star: true },
  { id: 'gar-peppers', word: 'pepper', plural: 'peppers', count: 3, cat: 'veg', color: 'red and yellow', star: true },
  { id: 'gar-pumpkins', word: 'pumpkin', plural: 'pumpkins', count: 3, cat: 'veg', color: 'orange' },
];

// Image filenames follow Natalya's Drive names (Apples.webp, Corn.webp, …).
const IMG = (name) => `/images/garden/${name}.webp`;
const BANK_IMAGES = {
  'gar-apples': 'Apples', 'gar-bananas': 'Bananas', 'gar-grapes': 'Grapes',
  'gar-lemon': 'Lemon', 'gar-oranges': 'Oranges', 'gar-carrots': 'Carrots',
  'gar-cucumbers': 'Cucumbers', 'gar-potatoes': 'Potatoes', 'gar-corn': 'Corn',
  'gar-tomatoes': 'Tomatoes', 'gar-peppers': 'Peppers',
  'gar-strawberries': 'Strawberries', 'gar-pears': 'Pears', 'gar-pumpkins': 'Pumpkins',
  'gar-orange-juice': 'Orange Juice', 'gar-apple-juice': 'Apple Juice',
};
for (const c of bank) c.image = IMG(BANK_IMAGES[c.id]);

// ── Taste cards (slot 6 — "Is this sweet or sour?") ──
// The script alternates peach/sweet and lemon/sour; buildDeck flips a coin.
// They ride the name tier: the key word IS the taste word, and the pack's
// builders swap in the taste question and "It is sweet./It is sour." lines.
const tasteCards = [
  { id: 'gar-taste-peach', word: 'sweet', plural: 'sweet', count: 1, taste: true, image: IMG('Peach'), usesItem: null },
  { id: 'gar-taste-lemon', word: 'sour', plural: 'sour', count: 1, taste: true, image: IMG('Lemon'), usesItem: 'gar-lemon' },
];

// ── Rainbow cards (slots 7–8, fixed) ──
const RAINBOW_COLORS = ['red', 'orange', 'yellow', 'green', 'blue', 'indigo', 'violet'];
const rainbowCountCard = {
  id: 'gar-rainbow-count', word: 'color', plural: 'colors', count: 7, image: IMG('Rainbow'),
};
const rainbowNameCard = {
  id: 'gar-rainbow-name', word: 'rainbow', plural: 'rainbows', count: 1, image: IMG('Rainbow'),
  listWords: RAINBOW_COLORS,
  // STT reality for 3–6 year-olds: five of the seven color words heard counts
  // as correct ("also counts: the seven colors, any order" — indigo/violet
  // are the ones small kids and the recognizer drop). Tune here if needed.
  // Any order; matches ACCUMULATE across turns (kids who get cut off can
  // keep going). NO purple→violet synonym — purple is not a rainbow color
  // (Natalya 2026-08-18, Mark's test).
  listMin: 5,
};

// ── "Do you like fruits/vegetables?" cards (slot 9 — yesno tier) ──
const likeCards = {
  fruit: { id: 'gar-like-fruits', word: 'fruits', plural: 'fruits', count: 2, image: IMG('Fruits') },
  veg: { id: 'gar-like-vegetables', word: 'vegetables', plural: 'vegetables', count: 2, image: IMG('Vegetables') },
};

const pool = [...bank, ...tasteCards, rainbowCountCard, rainbowNameCard, likeCards.fruit, likeCards.veg];

// Named exports for tts/gen-garden-audio.mjs — the generator derives every
// frog line from these banks via the pack's own builders (no drift possible).
export { bank, tasteCards, rainbowCountCard, rainbowNameCard, likeCards, RAINBOW_COLORS };

// ── Session state the favorite finale depends on (which category slot 9
// asked). buildDeck writes it; the favorite() function below reads it. The
// engine rebuilds the deck on every restart, so this always matches the
// session on screen. Default 'veg' → favorite fruit (the doc's example).
let likeCategory = 'veg';
export const getLikeCategory = () => likeCategory; // for tests/debugging

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

// FIXED slot order per the script's Question-flow table. Always 10 questions
// (9 cards + the starred favorite finale).
function buildDeck() {
  // Slot 6 first — its item is excluded from naming/color so the same picture
  // never appears twice in a session.
  const tasteCard = tasteCards[Math.floor(Math.random() * tasteCards.length)];
  const excluded = new Set(tasteCard.usesItem ? [tasteCard.usesItem] : []);

  // Slots 1–2 — name one fruit, one vegetable.
  const namedFruit = shuffle(bank.filter((c) => c.cat === 'fruit' && !excluded.has(c.id)))[0];
  const namedVeg = shuffle(bank.filter((c) => c.cat === 'veg' && !excluded.has(c.id)))[0];
  excluded.add(namedFruit.id); excluded.add(namedVeg.id);

  // Slots 3–5 — color questions on three NEW items. ★ multi-color items are
  // favored (script), and the five items stay fruit/veg balanced (2–3 fruits).
  const candidates = [
    ...shuffle(bank.filter((c) => c.color && c.star && !excluded.has(c.id))),
    ...shuffle(bank.filter((c) => c.color && !c.star && !excluded.has(c.id))),
  ]; // c.color guard: the juice cards are naming-only
  const colorCards = [];
  let fruits = 1; let vegs = 1; // slots 1–2
  for (const c of candidates) {
    if (colorCards.length === 3) break;
    if (c.cat === 'fruit' ? fruits >= 3 : vegs >= 3) continue;
    colorCards.push(c);
    if (c.cat === 'fruit') fruits += 1; else vegs += 1;
  }
  // Safety top-up (can only trigger if the bank shrinks): take anything left.
  for (const c of candidates) {
    if (colorCards.length === 3) break;
    if (!colorCards.includes(c)) colorCards.push(c);
  }

  // Slot 9 — like fruits or vegetables; slot 10 (favorite) asks the OTHER one.
  likeCategory = Math.random() < 0.5 ? 'fruit' : 'veg';

  return [
    { ...namedFruit, tiers: ['name'] },
    { ...namedVeg, tiers: ['name'] },
    ...colorCards.map((c) => ({ ...c, tiers: ['color'] })),
    { ...tasteCard, tiers: ['name'] },
    { ...rainbowCountCard, tiers: ['count'] },
    { ...rainbowNameCard, tiers: ['list'] },
    { ...(likeCategory === 'fruit' ? likeCards.fruit : likeCards.veg), tiers: ['yesno'] },
  ];
}

// ─── String builders (engine prefers these when present) ───
const art = (c) => (c.article === '' ? '' : `${c.article ?? (/^[aeiou]/.test(c.word) ? 'an' : 'a')} `);
const isMulti = (c) => c.count > 1;

function q(card, tier) {
  if (card.taste) {
    return { audio: '/audio/gar-taste-ask.mp3', text: 'Is this sweet or sour?' };
  }
  if (tier === 'color') {
    return isMulti(card)
      ? { audio: '/audio/gar-color-ask-p.mp3', text: 'What color are these?' }
      : { audio: '/audio/gar-color-ask-s.mp3', text: 'What color is this?' };
  }
  if (tier === 'count') {
    return { audio: '/audio/gar-rainbow-count-ask.mp3', text: 'How many colors are in the rainbow?' };
  }
  if (tier === 'list') {
    return { audio: '/audio/gar-rainbow-name-ask.mp3', text: 'Can you name the colors in the rainbow?' };
  }
  if (tier === 'yesno') {
    return { audio: `/audio/${card.id}-ask.mp3`, text: `Do you like ${card.word}?` };
  }
  // name tier — the shared Farm asks (already cached), per the picture.
  return isMulti(card)
    ? { audio: '/audio/what-are-these.mp3', text: 'What are these?' }
    : { audio: '/audio/what-is-this.mp3', text: 'What is this?' };
}

function sentence(card, tier) {
  if (card.taste) return { sentence: `It is ${card.word}.`, highlight: card.word };
  if (tier === 'name') {
    return isMulti(card)
      ? { sentence: `These are ${card.plural}.`, highlight: card.plural }
      : { sentence: `This is ${art(card)}${card.word}.`, highlight: card.word };
  }
  if (tier === 'color') {
    // colorWord = the taught color when more count as correct (grapes:
    // matches 'blue and purple', teaches "…are blue.").
    const cw = card.colorWord || card.color;
    return isMulti(card)
      ? { sentence: `These ${card.plural} are ${cw}.`, highlight: cw }
      : { sentence: `This ${card.word} is ${cw}.`, highlight: cw };
  }
  // "There are seven colors in the rainbow." — Natalya 2026-08-18.
  if (tier === 'count') return { sentence: 'There are seven colors in the rainbow.', highlight: 'seven' };
  if (tier === 'list') {
    return { sentence: 'Red, orange, yellow, green, blue, indigo, violet.', highlight: null };
  }
  return null; // yesno sentences depend on the answer → yesNoSentence
}

function correctLine(card, tier) {
  const s = sentence(card, tier);
  if (!s) return null;
  if (tier === 'list') return { audio: `/audio/${card.id}-correct.mp3`, text: 'Yes! Red, orange, yellow, green, blue, indigo, violet!' };
  const suffix = tier === 'color' ? '-color-correct' : '-correct';
  return { audio: `/audio/${card.id}${suffix}.mp3`, text: `Yes! ${s.sentence}` };
}

function revealLine(card, tier) {
  const s = sentence(card, tier);
  if (!s) return null;
  const suffix = tier === 'color' ? '-color-reveal' : '-reveal';
  return { audio: `/audio/${card.id}${suffix}.mp3`, text: `${s.sentence} Repeat after me. ${s.sentence}` };
}

// The full sentence the yes/no ladder teaches, per the child's OWN answer
// ("Yes, I like vegetables." / "No, I do not like vegetables." — script).
function yesNoSentence(card, saidYes) {
  return saidYes
    ? { sentence: `Yes, I like ${card.word}.`, highlight: card.word }
    : { sentence: `No, I do not like ${card.word}.`, highlight: card.word };
}

function yesNoReveal(card, saidYes) {
  const s = yesNoSentence(card, saidYes).sentence;
  return { audio: `/audio/${card.id}-${saidYes ? 'yes' : 'no'}-reveal.mp3`, text: `${s} Repeat after me. ${s}` };
}

// ── Favorite finale (slot 10) — DYNAMIC: the opposite category of slot 9 ──
// Any real fruit/vegetable is accepted; Frog names it back warmly ("I love
// strawberries too! Great choice." — the engine's default celebrateLine is
// exactly the script's wording). Bank words + the three awaiting-image words
// have pre-baked audio; extras celebrate via the live named Puck line.
// EVERY curated favorite is a full option with pre-baked celebration audio
// (2026-08-18, after Natalya's watermelon report): options play cached WAVs
// via speak(), so no favorite answer on this list ever depends on the live
// /api/speak path. Avocado + watermelon added per her email. Extras is empty
// by design — only judge-approved words beyond this list use the live line.
const favoriteOptions = {
  fruit: [
    { word: 'apple', plural: 'apples' }, { word: 'banana', plural: 'bananas' },
    { word: 'grape', plural: 'grapes' }, { word: 'lemon', plural: 'lemons' },
    { word: 'orange', plural: 'oranges' }, { word: 'peach', plural: 'peaches' },
    { word: 'strawberry', plural: 'strawberries' }, { word: 'pear', plural: 'pears' },
    { word: 'avocado', plural: 'avocados' }, { word: 'watermelon', plural: 'watermelons' },
    { word: 'pineapple', plural: 'pineapples' }, { word: 'cherry', plural: 'cherries' },
    { word: 'mango', plural: 'mangoes' }, { word: 'blueberry', plural: 'blueberries' },
    { word: 'raspberry', plural: 'raspberries' }, { word: 'kiwi', plural: 'kiwis' },
    { word: 'plum', plural: 'plums' }, { word: 'apricot', plural: 'apricots' },
    { word: 'melon', plural: 'melons' },
  ],
  veg: [
    { word: 'carrot', plural: 'carrots' }, { word: 'cucumber', plural: 'cucumbers' },
    { word: 'potato', plural: 'potatoes' }, { word: 'corn', plural: 'corn' },
    { word: 'tomato', plural: 'tomatoes' }, { word: 'pepper', plural: 'peppers' },
    { word: 'pumpkin', plural: 'pumpkins' }, { word: 'broccoli', plural: 'broccoli' },
    { word: 'peas', plural: 'peas' }, { word: 'onion', plural: 'onions' },
    { word: 'lettuce', plural: 'lettuce' }, { word: 'cabbage', plural: 'cabbage' },
    { word: 'spinach', plural: 'spinach' }, { word: 'celery', plural: 'celery' },
    { word: 'cauliflower', plural: 'cauliflower' }, { word: 'zucchini', plural: 'zucchini' },
    { word: 'radish', plural: 'radishes' }, { word: 'beet', plural: 'beets' },
    { word: 'squash', plural: 'squash' },
  ],
};
export { favoriteOptions };

const favoriteExtras = { fruit: [], veg: [] };

// Exported for tts/gen-garden-audio.mjs, which generates BOTH categories'
// lines (a session only ever uses one, but the audio library needs both).
export function favoriteForCategory(cat) {
  const isFruit = cat === 'fruit';
  return {
    question: isFruit ? 'What is your favorite fruit?' : 'What is your favorite vegetable?',
    askAudio: isFruit ? '/audio/gar-favorite-fruit-ask.mp3' : '/audio/gar-favorite-vegetable-ask.mp3',
    image: isFruit ? IMG('Fruits') : IMG('Vegetables'),
    judgeKind: isFruit ? 'fruit' : 'vegetable',
    options: favoriteOptions[cat],
    extras: favoriteExtras[cat],
    synonyms: {},
    audioFor: (word) => `/audio/gar-favorite-${word}.mp3`,
    missKnown: isFruit
      ? { line: "That's not a fruit.", audio: '/audio/gar-not-fruit.mp3' }
      : { line: "That's not a vegetable.", audio: '/audio/gar-not-vegetable.mp3' },
    missUnknown: isFruit
      ? { line: "That's not a fruit.", audio: '/audio/gar-not-fruit.mp3' }
      : { line: "That's not a vegetable.", audio: '/audio/gar-not-vegetable.mp3' },
    hint: isFruit
      ? { line: "Let's pick together. How about an apple or a banana?", audio: '/audio/gar-hint-fruit.mp3' }
      : { line: "Let's pick together. How about a carrot or a tomato?", audio: '/audio/gar-hint-vegetable.mp3' },
    // Slot 10 IS question 10 in this unit (Farm's favorite was a bonus after
    // its 10) — so a real answer earns the 10th star.
    earnsStar: true,
  };
}

function favorite() {
  return favoriteForCategory(likeCategory === 'fruit' ? 'veg' : 'fruit'); // OPPOSITE of slot 9
}

const namingWords = bank.map((c) => c.plural).join(', ');

export default {
  id: 'garden',
  title: 'Garden Adventure',
  pool,
  buildDeck,
  q, sentence, correctLine, revealLine,
  yesNoSentence, yesNoReveal,
  yesNoVerb: 'like', // the ladder sentence is "I like …" (School keeps 'have')
  // 'love' counts too — "I love fruits, they're so sweet" is a full-credit
  // answer, not a re-ask (Polina's play test, 2026-08-18).
  yesNoVerbs: ['like', 'love'],

  hasFavorite: true,
  favorite, // FUNCTION — resolved per session (fruit vs vegetable, ↑)

  // ── Closing (3+ stars) — no name question in this unit, so no name. ──
  closing: {
    minStars: 3,
    line: () => 'You are a great gardener!',
    audioNoName: '/audio/gar-great-gardener.mp3',
  },

  // Cartoon garden backdrop (Polina, 2026-08-18): raised veggie beds, fruit
  // trees, rainbow — 1672×941 like the farm bg, shipped as ~165KB webp (q82,
  // PNG source alongside, same convention as farm/playground). The Drive
  // folder's Garden.png (washed-out photo still) was rejected.
  background: '/images/garden-bg-v3.webp', // v3 2026-10-20: open grass under the frog, rainbow upper-left (tracker #4)

  sttNameContext: `a fruit or vegetable name, singular or plural (${namingWords}), or a taste word (sweet, sour)`,
  sttColorContext: 'a color word (red, orange, yellow, green, blue, purple, brown)',
  sttListContext: 'a young child listing rainbow colors (red, orange, yellow, green, blue, indigo, violet)',
  sttCountContext: 'a number from one to ten',
  sttYesNoContext: 'a yes or no answer from a young child, maybe as a full sentence like "yes, I like vegetables"',
  sttFavoriteContext: 'a fruit or vegetable name a young child likes',
  judgeTopic: 'a garden picture card — fruits and vegetables (naming and colors), '
    + 'taste (sweet or sour), and the rainbow (seven colors)',
  judgeHints: 'Spanish: manzana=apple, plátano/banana=banana, uvas=grapes, limón=lemon, '
    + 'naranja=orange, fresa=strawberry, pera=pear, durazno/melocotón=peach, zanahoria=carrot, '
    + 'pepino=cucumber, papa/patata=potato, maíz=corn, tomate=tomato, pimiento=pepper, '
    + 'calabaza=pumpkin; rojo=red, amarillo=yellow, anaranjado=orange, verde=green, '
    + 'azul=blue, morado=purple, marrón/café=brown; dulce=sweet, agrio/ácido=sour; siete=seven. '
    + 'Russian: яблоко=apple, банан=banana, виноград=grapes, лимон=lemon, апельсин=orange, '
    + 'клубника=strawberry, груша=pear, персик=peach, морковь=carrot, огурец=cucumber, '
    + 'картошка=potato, кукуруза=corn, помидор=tomato, перец=pepper, тыква=pumpkin; '
    + 'красный=red, жёлтый=yellow, оранжевый=orange, зелёный=green, синий=blue, фиолетовый=purple, '
    + 'коричневый=brown; сладкий=sweet, кислый=sour; семь=seven',
};
